import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { userAPI } from "../Redux/services/UserService";
import { useNavigate, useParams } from "react-router-dom";

interface EditUserForm {
    login: string;
    password?: string;
    full_name: string;
    role: string;
}

const EditUser: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { data: user, isLoading, isError } = userAPI.useFetchUserByIdQuery(id!);
    const [updateUser] = userAPI.useUpdateUserMutation();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<EditUserForm>();

    useEffect(() => {
        if (user) {
            reset({
                login: user.login,
                full_name: user.full_name,
                role: user.role,
            });
        }
    }, [user, reset]);

    const onSubmit = async (data: EditUserForm) => {
        try {
            const body = {
                login: data.login,
                full_name: data.full_name,
                role: data.role,
                ...(data.password ? { password: data.password } : {}) // добавляем только если есть
            }
            await updateUser({ id, body });
            navigate("/admin");
        } catch (err) {
            console.error("Помилка при оновленні користувача", err);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-[70vh] text-gray-600">
                Завантаження...
            </div>
        );
    }

    if (isError || !user) {
        return (
            <div className="flex items-center justify-center h-[70vh] text-red-500">
                Не вдалося завантажити користувача.
            </div>
        );
    }

    return (
        <div className="flex justify-center items-center min-h-[70vh] bg-gray-100">
            <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-md border border-gray-200">
                <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">
                    Редагування користувача
                </h2>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    {/* Повне ім’я */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Повне ім’я
                        </label>
                        <input
                            type="text"
                            {...register("full_name", { required: "Вкажіть повне ім’я" })}
                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                errors.full_name
                                    ? "border-red-500 focus:ring-red-400"
                                    : "border-gray-300 focus:ring-blue-400"
                            }`}
                            placeholder="Введіть повне ім’я"
                        />
                        {errors.full_name && (
                            <p className="text-red-500 text-sm mt-1">{errors.full_name.message}</p>
                        )}
                    </div>

                    {/* Логін */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Логін
                        </label>
                        <input
                            type="text"
                            {...register("login", { required: "Вкажіть логін" })}
                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                errors.login
                                    ? "border-red-500 focus:ring-red-400"
                                    : "border-gray-300 focus:ring-blue-400"
                            }`}
                            placeholder="Введіть логін"
                        />
                        {errors.login && (
                            <p className="text-red-500 text-sm mt-1">{errors.login.message}</p>
                        )}
                    </div>

                    {/* Пароль (опціонально) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Новий пароль (необов’язково)
                        </label>
                        <input
                            type="password"
                            {...register("password", {
                                minLength: { value: 6, message: "Мінімум 6 символів" },
                            })}
                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                errors.password
                                    ? "border-red-500 focus:ring-red-400"
                                    : "border-gray-300 focus:ring-blue-400"
                            }`}
                            placeholder="Введіть новий пароль"
                        />
                        {errors.password && (
                            <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                        )}
                    </div>

                    {/* Роль */}


                    {/* Кнопки */}
                    <div className="flex justify-between mt-6">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition"
                        >
                            Назад
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                        >
                            Зберегти
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUser;
